<template>
  <div class="app-container refund-page">
    <el-alert
      title="资金操作会写入不可变审计日志。退款金额、支付流水与微信回调不一致时，后端会自动阻断。"
      type="warning"
      :closable="false"
      show-icon
    />

    <el-card class="query-card">
      <el-form :inline="true" :model="query" size="small">
        <el-form-item label="订单类型">
          <el-select v-model="query.order_category" clearable placeholder="全部类型" style="width:160px">
            <el-option label="普通订单" value="order" />
            <el-option label="企业员工订单" value="employee_order" />
          </el-select>
        </el-form-item>
        <el-form-item label="订单号">
          <el-input v-model.trim="query.order_no" clearable maxlength="100" placeholder="精确订单号" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="退款状态">
          <el-select v-model="query.status" clearable placeholder="全部状态" style="width:140px">
            <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">查询流水</el-button>
          <el-button @click="resetQuery">重置</el-button>
          <el-button v-if="canManage" type="danger" plain @click="openCreate">发起部分退款</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-if="preview.order_no" class="summary-card">
      <div slot="header" class="card-header">
        <span>订单资金概览：{{ preview.order_no }}</span>
        <el-tag :type="refundStatusType(preview.refund_status)">{{ orderRefundStatusText(preview.refund_status) }}</el-tag>
      </div>
      <el-row :gutter="16">
        <el-col v-for="item in summaryItems" :key="item.label" :xs="12" :sm="6">
          <div class="amount-box" :class="item.className">
            <div class="amount-label">{{ item.label }}</div>
            <div class="amount-value">¥{{ money(item.value) }}</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <el-card class="table-card">
      <el-tabs v-model="activeTab" @tab-click="handleTabChange">
        <el-tab-pane label="退款流水" name="refunds">
          <el-table :data="records" border stripe v-loading="loading" empty-text="暂无退款流水">
            <el-table-column prop="refund_no" label="退款单号" min-width="210" fixed="left" />
            <el-table-column prop="order_no" label="订单号" min-width="180" />
            <el-table-column label="类型" width="120">
              <template slot-scope="scope">{{ categoryText(scope.row.order_category) }}</template>
            </el-table-column>
            <el-table-column label="退款金额" width="120" align="right">
              <template slot-scope="scope"><strong>¥{{ money(scope.row.refund_amount) }}</strong></template>
            </el-table-column>
            <el-table-column label="原支付金额" width="125" align="right">
              <template slot-scope="scope">¥{{ money(scope.row.pay_amount) }}</template>
            </el-table-column>
            <el-table-column label="退款前/后" width="175" align="right">
              <template slot-scope="scope">¥{{ money(scope.row.refunded_amount_before) }} / ¥{{ money(scope.row.refunded_amount_after) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="105">
              <template slot-scope="scope"><el-tag :type="statusType(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag></template>
            </el-table-column>
            <el-table-column prop="operator_name" label="操作人" width="120" />
            <el-table-column prop="retry_count" label="重试次数" width="90" align="center" />
            <el-table-column prop="remark" label="退款原因" min-width="220" show-overflow-tooltip />
            <el-table-column prop="failure_message" label="失败原因" min-width="220" show-overflow-tooltip />
            <el-table-column prop="created_at" label="创建时间" width="165" />
            <el-table-column prop="processing_at" label="处理时间" width="165" />
            <el-table-column prop="success_at" label="成功时间" width="165" />
            <el-table-column label="操作" width="230" fixed="right">
              <template slot-scope="scope">
                <el-button v-if="canManage && (scope.row.status === 1 || scope.row.status === 3)" size="mini" :loading="rowLoading(scope.row, 'query')" @click="queryWechat(scope.row)">查微信</el-button>
                <el-button v-if="canManage && scope.row.status === 3" type="warning" size="mini" :loading="rowLoading(scope.row, 'retry')" @click="retry(scope.row)">重试</el-button>
                <el-button v-if="canManage && (scope.row.status === 0 || scope.row.status === 3)" type="danger" size="mini" :loading="rowLoading(scope.row, 'close')" @click="closeRefund(scope.row)">关闭</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="pagination"
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="query.page"
            :page-size="query.limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="total"
            @size-change="changeSize"
            @current-change="changePage"
          />
        </el-tab-pane>

        <el-tab-pane label="资金审计日志" name="audit">
          <el-table :data="auditRecords" border stripe v-loading="auditLoading" empty-text="暂无审计日志">
            <el-table-column prop="id" label="ID" width="90" />
            <el-table-column prop="action" label="事件" min-width="210">
              <template slot-scope="scope">{{ actionText(scope.row.action) }}</template>
            </el-table-column>
            <el-table-column prop="order_no" label="订单号" min-width="180" />
            <el-table-column prop="refund_no" label="退款单号" min-width="210" />
            <el-table-column prop="operator_name" label="操作人" width="120" />
            <el-table-column prop="ip" label="IP" width="135" />
            <el-table-column prop="request_id" label="请求标识" min-width="160" show-overflow-tooltip />
            <el-table-column prop="created_at" label="记录时间" width="165" />
            <el-table-column label="金额与上下文" width="120">
              <template slot-scope="scope"><el-button size="mini" @click="showAudit(scope.row)">查看</el-button></template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="pagination"
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="auditQuery.page"
            :page-size="auditQuery.limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="auditTotal"
            @size-change="changeAuditSize"
            @current-change="changeAuditPage"
          />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-dialog title="发起部分退款" :visible.sync="createVisible" width="560px" :close-on-click-modal="false" @closed="clearCreate">
      <el-form ref="refundForm" :model="form" :rules="rules" label-width="125px">
        <el-form-item label="订单类型" prop="order_category">
          <el-select v-model="form.order_category" style="width:100%" @change="clearPreview">
            <el-option label="普通订单" value="order" />
            <el-option label="企业员工订单" value="employee_order" />
          </el-select>
        </el-form-item>
        <el-form-item label="订单号" prop="order_no">
          <el-input v-model.trim="form.order_no" maxlength="100"><el-button slot="append" :loading="previewLoading" @click="loadPreview">核对金额</el-button></el-input>
        </el-form-item>
        <template v-if="preview.order_no">
          <el-alert
            :title="preview.partial_refund_allowed ? '订单状态：已完成，可发起部分退款' : (preview.partial_refund_disabled_reason || '当前订单禁止部分退款')"
            :type="preview.partial_refund_allowed ? 'success' : 'error'"
            :closable="false"
            show-icon
          />
          <el-alert :title="`剩余可退 ¥${money(preview.available_refund_amount)}；已退 ¥${money(preview.refunded_amount)}；退款中 ¥${money(preview.refunding_amount)}`" type="info" :closable="false" show-icon />
          <el-form-item label="本次退款金额" prop="refund_amount" class="amount-input">
            <el-input-number v-model="form.refund_amount" :precision="2" :step="0.01" :min="0.01" :max="Number(preview.available_refund_amount || 0)" />
          </el-form-item>
          <el-form-item label="退款原因" prop="remark">
            <el-input v-model.trim="form.remark" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请填写可供财务审计的具体原因" />
          </el-form-item>
        </template>
      </el-form>
      <div slot="footer">
        <el-button @click="createVisible=false">取消</el-button>
        <el-button type="danger" :disabled="!preview.order_no || !preview.partial_refund_allowed" :loading="submitting" @click="submit">确认提交退款</el-button>
      </div>
    </el-dialog>

    <el-dialog title="审计详情" :visible.sync="auditVisible" width="680px">
      <el-form v-if="selectedAudit" label-width="130px" class="audit-detail">
        <el-form-item label="事件">{{ actionText(selectedAudit.action) }}</el-form-item>
        <el-form-item label="订单号">{{ selectedAudit.order_no || '-' }}</el-form-item>
        <el-form-item label="退款单号">{{ selectedAudit.refund_no || '-' }}</el-form-item>
        <el-form-item label="操作人">{{ selectedAudit.operator_name || 'system' }}</el-form-item>
        <el-form-item label="上下文"><pre>{{ prettyContext(selectedAudit.context) }}</pre></el-form-item>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  data() {
    return {
      activeTab: 'refunds',
      query: { page: 1, limit: 20, order_no: '', order_category: '', status: '' },
      auditQuery: { page: 1, limit: 20 },
      records: [], total: 0, loading: false,
      auditRecords: [], auditTotal: 0, auditLoading: false,
      preview: {}, previewLoading: false,
      createVisible: false, submitting: false,
      auditVisible: false, selectedAudit: null,
      actionLoading: {},
      form: this.emptyForm(),
      statusOptions: [
        { label: '待处理', value: 0 }, { label: '处理中', value: 1 }, { label: '成功', value: 2 },
        { label: '失败', value: 3 }, { label: '已关闭', value: 4 }
      ],
      rules: {
        order_category: [{ required: true, message: '请选择订单类型', trigger: 'change' }],
        order_no: [{ required: true, message: '请输入订单号', trigger: 'blur' }],
        refund_amount: [{ required: true, message: '请输入退款金额', trigger: 'change' }],
        remark: [{ required: true, min: 5, max: 500, message: '退款原因需填写5到500个字符', trigger: 'blur' }]
      }
    }
  },
  computed: {
    canManage() {
      const permissions = this.$store.getters.permissions || []
      return permissions.some(item => ['all', 'admin', 'order.refund.manage'].indexOf(item) >= 0)
    },
    summaryItems() {
      return [
        { label: '支付金额', value: this.preview.pay_amount },
        { label: '累计已退', value: this.preview.refunded_amount, className: 'success' },
        { label: '退款处理中', value: this.preview.refunding_amount, className: 'warning' },
        { label: '剩余可退', value: this.preview.available_refund_amount, className: 'danger' }
      ]
    }
  },
  created() { this.loadRecords() },
  methods: {
    emptyForm() { return { order_no: '', order_category: 'order', refund_amount: 0.01, remark: '', idempotency_key: '' } },
    makeKey() { return 'admin-refund-' + Date.now() + '-' + Math.random().toString(36).slice(2, 14) },
    cleanParams(source) {
      return Object.keys(source).reduce((result, key) => {
        if (source[key] !== '' && source[key] !== null && source[key] !== undefined) result[key] = source[key]
        return result
      }, {})
    },
    money(value) { const number = Number(value); return Number.isFinite(number) ? number.toFixed(2) : '0.00' },
    categoryText(value) { return value === 'employee_order' ? '企业员工订单' : '普通订单' },
    statusText(value) { return ({ 0: '待处理', 1: '处理中', 2: '成功', 3: '失败', 4: '已关闭' })[Number(value)] || '未知' },
    statusType(value) { return ({ 0: 'info', 1: 'warning', 2: 'success', 3: 'danger', 4: 'info' })[Number(value)] || 'info' },
    orderRefundStatusText(value) { return ({ 0: '未退款', 1: '全额退款中', 2: '全额退款成功', 3: '退款失败', 4: '部分退款中', 5: '部分退款成功' })[Number(value)] || '未知' },
    refundStatusType(value) { return ({ 0: 'info', 1: 'warning', 2: 'success', 3: 'danger', 4: 'warning', 5: 'success' })[Number(value)] || 'info' },
    actionText(value) {
      return ({ payment_succeeded: '支付成功', employee_payment_succeeded: '企业订单支付成功', rebate_credited: '返佣到账', partial_refund_created: '创建部分退款', partial_refund_submitted: '部分退款已提交微信', partial_refund_succeeded: '部分退款成功', partial_refund_failed: '部分退款失败', partial_refund_closed: '部分退款关闭', full_refund_requested: '申请全额退款', full_refund_forced: '强制全额退款', full_refund_provider_accepted: '微信受理全额退款', full_refund_succeeded: '全额退款成功', full_refund_failed: '全额退款失败', employee_full_refund_provider_accepted: '微信受理企业订单退款', employee_full_refund_succeeded: '企业订单退款成功', employee_full_refund_failed: '企业订单退款失败', order_status_forced: '强制修改订单状态' })[value] || value
    },
    search() { this.query.page = 1; this.loadRecords(); if (this.activeTab === 'audit') this.loadAudit() },
    resetQuery() { this.query = { page: 1, limit: 20, order_no: '', order_category: '', status: '' }; this.preview = {}; this.search() },
    loadRecords() {
      this.loading = true
      request({ url: '/api/backend/order/refund-list', method: 'get', params: this.cleanParams(this.query) })
        .then(res => { this.records = res.data.items || res.data.data || []; this.total = Number(res.data.total || 0) })
        .finally(() => { this.loading = false })
    },
    loadAudit() {
      this.auditLoading = true
      const params = Object.assign({}, this.auditQuery, { order_no: this.query.order_no })
      request({ url: '/api/backend/order/refund-audit-logs', method: 'get', params: this.cleanParams(params) })
        .then(res => { this.auditRecords = res.data.items || res.data.data || []; this.auditTotal = Number(res.data.total || 0) })
        .finally(() => { this.auditLoading = false })
    },
    handleTabChange(tab) { if (tab.name === 'audit') this.loadAudit() },
    changeSize(value) { this.query.limit = value; this.query.page = 1; this.loadRecords() },
    changePage(value) { this.query.page = value; this.loadRecords() },
    changeAuditSize(value) { this.auditQuery.limit = value; this.auditQuery.page = 1; this.loadAudit() },
    changeAuditPage(value) { this.auditQuery.page = value; this.loadAudit() },
    openCreate() { this.form = this.emptyForm(); this.form.order_no = this.query.order_no; this.form.order_category = this.query.order_category || 'order'; this.form.idempotency_key = this.makeKey(); this.preview = {}; this.createVisible = true },
    clearCreate() { this.preview = {}; this.form = this.emptyForm() },
    clearPreview() { this.preview = {}; this.form.refund_amount = 0.01 },
    loadPreview() {
      if (!this.form.order_no) return this.$message.warning('请输入订单号')
      this.previewLoading = true
      request({ url: '/api/backend/order/refund-preview', method: 'get', params: { order_no: this.form.order_no, order_category: this.form.order_category } })
        .then(res => { this.preview = res.data; this.form.refund_amount = Math.min(0.01, Number(res.data.available_refund_amount || 0)) })
        .finally(() => { this.previewLoading = false })
    },
    submit() {
      this.$refs.refundForm.validate(valid => {
        if (!valid || !this.preview.order_no) return
        if (!this.preview.partial_refund_allowed) return this.$message.error('仅已完成状态的订单允许部分退款')
        const amount = Number(this.form.refund_amount)
        const available = Number(this.preview.available_refund_amount)
        if (!Number.isFinite(amount) || amount <= 0 || amount > available) return this.$message.error('退款金额超出剩余可退范围')
        const message = `订单：${this.form.order_no}\n支付：¥${this.money(this.preview.pay_amount)}\n已退：¥${this.money(this.preview.refunded_amount)}\n本次：¥${amount.toFixed(2)}\n提交后不可直接撤销，确认继续？`
        this.$confirm(message, '高风险资金操作确认', { type: 'warning', confirmButtonText: '确认退款', dangerouslyUseHTMLString: false })
          .then(() => {
            this.submitting = true
            const confirmedAmount = amount.toFixed(2)
            const data = Object.assign({}, this.form, {
              refund_amount: confirmedAmount,
              confirmed_refund_amount: confirmedAmount
            })
            return request({ url: '/api/backend/order/partial-refund', method: 'post', data })
          })
          .then(() => { this.$message.success('退款申请已创建'); this.createVisible = false; this.query.order_no = this.form.order_no; this.query.order_category = this.form.order_category; this.search() })
          .finally(() => { this.submitting = false })
      })
    },
    rowKey(row, action) { return row.refund_no + ':' + action },
    rowLoading(row, action) { return Boolean(this.actionLoading[this.rowKey(row, action)]) },
    runRowAction(row, action, requestConfig, successMessage) {
      const key = this.rowKey(row, action)
      this.$set(this.actionLoading, key, true)
      return request(requestConfig).then(() => { this.$message.success(successMessage); this.loadRecords(); if (this.activeTab === 'audit') this.loadAudit() })
        .finally(() => { this.$delete(this.actionLoading, key) })
    },
    queryWechat(row) { return this.runRowAction(row, 'query', { url: '/api/backend/order/refund-query', method: 'post', data: { refund_no: row.refund_no } }, '微信退款状态已同步') },
    retry(row) {
      return this.$confirm(`退款单 ${row.refund_no} 将使用同一商户退款单号重试，确认继续？`, '退款重试确认', { type: 'warning' })
        .then(() => this.runRowAction(row, 'retry', { url: '/api/backend/order/refund-retry', method: 'post', data: { refund_no: row.refund_no } }, '退款已重新提交'))
    },
    closeRefund(row) {
      return this.$confirm(`仅当微信未退款成功时才能关闭。关闭退款单 ${row.refund_no} 并释放 ¥${this.money(row.refund_amount)} 预占金额？`, '关闭退款确认', { type: 'warning' })
        .then(() => this.runRowAction(row, 'close', { url: '/api/backend/order/refund-close', method: 'post', data: { refund_no: row.refund_no } }, '退款单已关闭'))
    },
    showAudit(row) { this.selectedAudit = row; this.auditVisible = true },
    prettyContext(value) { return JSON.stringify(value || {}, null, 2) }
  }
}
</script>

<style lang="scss" scoped>
.refund-page { .query-card, .summary-card, .table-card { margin-top: 16px; } }
.card-header { display:flex; justify-content:space-between; align-items:center; }
.amount-box { padding:16px; border:1px solid #ebeef5; border-radius:4px; background:#fafafa; margin-bottom:8px; }
.amount-label { color:#909399; font-size:13px; }
.amount-value { margin-top:8px; color:#303133; font-size:24px; font-weight:600; }
.amount-box.success .amount-value { color:#67c23a; }
.amount-box.warning .amount-value { color:#e6a23c; }
.amount-box.danger .amount-value { color:#f56c6c; }
.amount-input { margin-top:18px; }
.pagination { margin-top:18px; text-align:right; }
.audit-detail pre { margin:0; padding:12px; max-height:420px; overflow:auto; white-space:pre-wrap; word-break:break-all; background:#f5f7fa; border-radius:4px; }
</style>
